import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Col,
  Button,
  ButtonGroup,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import Sortable from "sortablejs";

import PageHero from "components/PageHero";
import { useToast } from "hooks/toast";
import { useCompanies } from "hooks/companies";
import { useAuth } from "hooks/auth";
import { useActiveProduct } from "hooks/activeProduct";
import { getLayout, saveLayout, resetLayout } from "services/meuPainelApi";

import { ABAS, CATALOGO, CATALOGO_POR_ID, COLUNAS_POR_TAMANHO } from "./catalogo";
import {
  SUGESTAO_INICIAL,
  MAX_WIDGETS,
  sanitizarLayout,
  moverItem,
  trocarTamanho,
  adicionar,
  remover,
  widgetDisponivel,
} from "./layout";
import MontagemPreguicosa from "./MontagemPreguicosa";
import "./styles.css";

// Meu Painel (épico PO1-3437): cada usuário monta o próprio painel com os gráficos
// que já existem na Torre de Controle. A Torre (views/Empresa/Dashboard) não muda.
//
// - Layout salvo por usuário no backend (GET/PUT/DELETE /meu-painel/layout).
// - Cada widget busca os próprios dados ao montar; a MontagemPreguicosa só monta o
//   que chega perto da tela, e o que não está no painel nunca consulta.
// - A grade remonta quando empresa ou transportadora mudam (key), e todos recarregam.
// - Modo Personalizar (PO1-3440): arrastar (SortableJS), setas, P/M/G, remover e catálogo.
// - Widget com `produtos`/`permissao` no catálogo (PO1-3494) só aparece, no modal e na
//   grade, no produto ativo certo e para quem tem a permissão; o layout salvo não muda.

const normalizar = (texto) =>
  (texto || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default function MeuPainel() {
  const { addToast } = useToast();
  const { empresaId, transportadoraId } = useCompanies();
  const { user } = useAuth();
  const { activeProduct } = useActiveProduct();
  const disponivel = useCallback((def) => widgetDisponivel(def, { produto: activeProduct, user }), [activeProduct, user]);

  const [layout, setLayout] = useState(null);
  const [rascunho, setRascunho] = useState([]);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [catalogoAberto, setCatalogoAberto] = useState(false);
  const [confirmarRestaurar, setConfirmarRestaurar] = useState(false);
  const [busca, setBusca] = useState("");
  const gridRef = useRef(null);

  const avisar = useCallback(
    (type, title, description) =>
      addToast({
        place: "tr",
        type,
        title,
        description,
        icon:
          type === "danger"
            ? "tim-icons icon-alert-circle-exc"
            : "tim-icons icon-check-2",
      }),
    [addToast]
  );

  useEffect(() => {
    let ativo = true;
    getLayout()
      .then((salvo) => {
        if (!ativo) return;
        setLayout(sanitizarLayout(salvo || SUGESTAO_INICIAL, CATALOGO_POR_ID));
      })
      .catch(() => {
        if (!ativo) return;
        setLayout(sanitizarLayout(SUGESTAO_INICIAL, CATALOGO_POR_ID));
        avisar(
          "danger",
          "Não foi possível carregar o seu painel",
          "Mostrando a sugestão inicial. Tente recarregar a página."
        );
      });
    return () => {
      ativo = false;
    };
  }, [avisar]);

  const itens = editando ? rascunho : layout || [];
  const chaveGrade = `${empresaId || "todas"}-${transportadoraId || "todas"}`;

  // Arrastar só no modo edição. O SortableJS move o nó no DOM; devolvemos o nó ao
  // lugar original e deixamos o React reordenar pelo estado (senão o React se perde).
  useEffect(() => {
    if (!editando || !gridRef.current) return undefined;
    const sortable = Sortable.create(gridRef.current, {
      handle: ".meu-painel-handle",
      draggable: ".meu-painel-item",
      animation: 150,
      ghostClass: "meu-painel-ghost",
      onEnd: (evt) => {
        const { item, from, oldDraggableIndex, newDraggableIndex } = evt;
        if (oldDraggableIndex === newDraggableIndex) return;
        from.removeChild(item);
        from.insertBefore(item, from.children[oldDraggableIndex] || null);
        setRascunho((atual) => moverItem(atual, oldDraggableIndex, newDraggableIndex));
      },
    });
    return () => sortable.destroy();
  }, [editando, chaveGrade]);

  const iniciarEdicao = () => {
    setRascunho(layout || []);
    setEditando(true);
  };

  const cancelar = () => {
    setEditando(false);
    setRascunho([]);
  };

  const concluir = async () => {
    setSalvando(true);
    try {
      const salvo = await saveLayout(rascunho);
      setLayout(sanitizarLayout(salvo || rascunho, CATALOGO_POR_ID));
      setEditando(false);
      avisar("success", "Painel salvo", "Ele vai abrir assim no próximo acesso.");
    } catch (e) {
      avisar(
        "danger",
        "Não foi possível salvar o painel",
        "Suas alterações continuam aqui. Tente de novo em instantes."
      );
    } finally {
      setSalvando(false);
    }
  };

  const restaurar = async () => {
    setSalvando(true);
    try {
      await resetLayout();
      const sugestao = sanitizarLayout(SUGESTAO_INICIAL, CATALOGO_POR_ID);
      setLayout(sugestao);
      setRascunho([]);
      setEditando(false);
      setConfirmarRestaurar(false);
      avisar("success", "Painel restaurado", "Voltou para a sugestão inicial.");
    } catch (e) {
      avisar("danger", "Não foi possível restaurar o painel", "Tente de novo em instantes.");
    } finally {
      setSalvando(false);
    }
  };

  const noPainel = useMemo(() => {
    const ids = {};
    rascunho.forEach((item) => {
      ids[item.widgetId] = true;
    });
    return ids;
  }, [rascunho]);

  const gruposCatalogo = useMemo(() => {
    const termo = normalizar(busca.trim());
    return ABAS.map((aba) => ({
      ...aba,
      itens: CATALOGO.filter(
        (w) =>
          w.aba === aba.id &&
          disponivel(w) &&
          (!termo ||
            normalizar(w.titulo).includes(termo) ||
            normalizar(w.descricao).includes(termo))
      ),
    })).filter((grupo) => grupo.itens.length > 0);
  }, [busca, disponivel]);

  const acoes = editando ? (
    <>
      <Button
        color="info"
        className="btn-sm"
        onClick={() => setCatalogoAberto(true)}
        disabled={salvando}
      >
        + Adicionar widget
      </Button>
      <Button
        color="default"
        outline
        className="btn-sm"
        onClick={() => setConfirmarRestaurar(true)}
        disabled={salvando}
      >
        Voltar à sugestão inicial
      </Button>
      <Button color="default" outline className="btn-sm" onClick={cancelar} disabled={salvando}>
        Cancelar
      </Button>
      <Button color="success" className="btn-sm" onClick={concluir} disabled={salvando}>
        {salvando ? "Salvando..." : "Concluir"}
      </Button>
    </>
  ) : (
    <Button color="info" className="btn-sm" onClick={iniciarEdicao} disabled={!layout}>
      Personalizar
    </Button>
  );

  return (
    <div className={`content meu-painel-page${editando ? " meu-painel-page--editando" : ""}`}>
      {/* PO1-3750: faixa fixa enquanto edita, para não restar dúvida do modo. */}
      {editando && (
        <div className="meu-painel-faixa-edicao" role="status" data-testid="faixa-edicao">
          <span className="meu-painel-faixa-edicao__texto">
            <i className="tim-icons icon-pencil" aria-hidden="true" /> Editando o seu painel
            <span className="meu-painel-faixa-edicao__dica"> · as mudanças só valem depois de Concluir</span>
          </span>
          <span className="meu-painel-faixa-edicao__acoes">
            <Button color="default" outline className="btn-sm" onClick={cancelar} disabled={salvando}>
              Cancelar
            </Button>
            <Button color="success" className="btn-sm" onClick={concluir} disabled={salvando}>
              {salvando ? "Salvando..." : "Concluir"}
            </Button>
          </span>
        </div>
      )}
      <PageHero
        eyebrow="Torre de Controle"
        title="Meu Painel"
        description={
          editando
            ? "Arraste pelo ⠿, troque o tamanho em P, M ou G, remova com ✕ ou adicione pelo catálogo. As alterações valem só para você."
            : "Os gráficos da Torre de Controle que você escolheu, do seu jeito."
        }
        actions={acoes}
      />

      {layout === null ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spinner color="info" />
        </div>
      ) : (
        <>
          {itens.length === 0 && (
            <div className="meu-painel-vazio">
              {editando
                ? "Seu painel está vazio. Use + Adicionar widget para escolher os gráficos."
                : "Seu painel está vazio. Clique em Personalizar para escolher os gráficos."}
            </div>
          )}
          <div ref={gridRef} className="row" key={chaveGrade}>
            {itens.map((item, indice) => {
              const def = CATALOGO_POR_ID[item.widgetId];
              if (!def || !disponivel(def)) return null;
              const { Componente } = def;
              return (
                <Col
                  xs="12"
                  lg={COLUNAS_POR_TAMANHO[item.tamanho] || 12}
                  key={item.widgetId}
                  data-id={item.widgetId}
                  className={`meu-painel-item${editando ? " editando" : ""}`}
                >
                  {editando && (
                    <div className="meu-painel-barra" data-testid={`barra-${item.widgetId}`}>
                      <span
                        className="meu-painel-handle"
                        title="Arrastar para mudar de lugar"
                        aria-hidden="true"
                      >
                        ⠿
                      </span>
                      <span className="meu-painel-titulo" title={def.titulo}>
                        {def.titulo}
                      </span>
                      <ButtonGroup size="sm">
                        <Button
                          color="default"
                          outline
                          aria-label={`Mover ${def.titulo} para antes`}
                          disabled={indice === 0}
                          onClick={() => setRascunho((r) => moverItem(r, indice, indice - 1))}
                        >
                          ‹
                        </Button>
                        <Button
                          color="default"
                          outline
                          aria-label={`Mover ${def.titulo} para depois`}
                          disabled={indice === itens.length - 1}
                          onClick={() => setRascunho((r) => moverItem(r, indice, indice + 1))}
                        >
                          ›
                        </Button>
                      </ButtonGroup>
                      <ButtonGroup size="sm">
                        {def.tamanhos.map((tamanho) => (
                          <Button
                            key={tamanho}
                            color={item.tamanho === tamanho ? "info" : "default"}
                            outline={item.tamanho !== tamanho}
                            aria-pressed={item.tamanho === tamanho}
                            aria-label={`Tamanho ${tamanho} para ${def.titulo}`}
                            onClick={() =>
                              setRascunho((r) => trocarTamanho(r, item.widgetId, tamanho))
                            }
                          >
                            {tamanho}
                          </Button>
                        ))}
                      </ButtonGroup>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        aria-label={`Remover ${def.titulo}`}
                        onClick={() => setRascunho((r) => remover(r, item.widgetId))}
                      >
                        ✕
                      </Button>
                    </div>
                  )}
                  <div className="meu-painel-moldura">
                    <MontagemPreguicosa>
                      <Componente />
                    </MontagemPreguicosa>
                  </div>
                </Col>
              );
            })}
          </div>
        </>
      )}

      <Modal
        isOpen={catalogoAberto}
        toggle={() => setCatalogoAberto(false)}
        modalClassName="modal-align-top"
        size="lg"
        fade={false}
      >
        <ModalHeader toggle={() => setCatalogoAberto(false)} tag="h5">
          Adicionar widget
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <Input
            id="meu-painel-busca"
            type="search"
            placeholder="Buscar por nome: frete, transportadora, fatura..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            autoComplete="off"
          />
          <p className="meu-painel-contador" style={{ margin: "8px 0 0", fontSize: 12 }}>
            {rascunho.length} de no máximo {MAX_WIDGETS} widgets no painel.
          </p>
          {gruposCatalogo.length === 0 && (
            <p style={{ marginTop: 16 }}>Nenhum gráfico encontrado para "{busca}".</p>
          )}
          {gruposCatalogo.map((grupo) => (
            <div className="meu-painel-catalogo-grupo" key={grupo.id}>
              <h6>{grupo.nome}</h6>
              {grupo.itens.map((w) => {
                const jaEsta = Boolean(noPainel[w.id]);
                return (
                  <div className="meu-painel-catalogo-item" key={w.id}>
                    <div className="meu-painel-catalogo-texto">
                      {w.titulo}
                      <small>{w.descricao}</small>
                    </div>
                    <Button
                      size="sm"
                      color={jaEsta ? "success" : "info"}
                      outline={!jaEsta}
                      data-testid={`catalogo-${w.id}`}
                      title={jaEsta ? "Clique para tirar do painel" : undefined}
                      disabled={!jaEsta && rascunho.length >= MAX_WIDGETS}
                      onClick={() =>
                        setRascunho((r) =>
                          jaEsta ? remover(r, w.id) : adicionar(r, w.id, CATALOGO_POR_ID)
                        )
                      }
                    >
                      {jaEsta ? "No painel ✓" : "Adicionar"}
                    </Button>
                  </div>
                );
              })}
            </div>
          ))}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={confirmarRestaurar}
        toggle={() => (salvando ? null : setConfirmarRestaurar(false))}
        modalClassName="modal-align-top"
        fade={false}
        size="sm"
      >
        <ModalHeader toggle={() => (salvando ? null : setConfirmarRestaurar(false))} tag="h5">
          Voltar à sugestão inicial
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <p className="meu-painel-texto-modal" style={{ marginBottom: "1.25rem" }}>
            O seu painel volta para a sugestão inicial e as suas escolhas são apagadas.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button
              color="default"
              outline
              size="sm"
              onClick={() => setConfirmarRestaurar(false)}
              disabled={salvando}
            >
              Cancelar
            </Button>
            <Button color="danger" size="sm" onClick={restaurar} disabled={salvando}>
              {salvando ? "Restaurando..." : "Voltar à sugestão"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}
